import { createFileRoute } from '@tanstack/react-router'
import { Loader2, Save, Upload, User, X } from 'lucide-react'
import { useMemo, useState } from 'react'
import { toast } from 'sonner'
import { useMe, useUpdateMe } from '@/api/queries'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { EmptyState } from '@/components/ui/empty-state'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { PageBody, PageContainer, Section } from '@/components/ui/page-container'
import { PageHeader, SectionHeader } from '@/components/ui/page-header'
import { SkeletonList } from '@/components/ui/loading-state'
import ErrorFallback from '@/components/ErrorFallback'
import { useUser, type AppUser } from '@/hooks/useUser'
import { formatError } from '@/lib/format-error'
import { userDisplayName, userInitials } from '@/lib/user-display'
import type { DashboardMe } from '@/api/queries'

export const Route = createFileRoute('/_authenticated/account')({
  component: AccountPage,
  errorComponent: ErrorFallback,
})

export function AccountPage() {
  const { data: me, isLoading, error } = useMe()
  const { user } = useUser()
  const profile = profileDetails(user, me)

  return (
    <PageContainer width="narrow">
      <PageHeader
        eyebrow="Settings"
        title="Account"
        description="Your profile and workspace membership details."
      />
      <PageBody>
        <Section aria-label="Profile">
          <SectionHeader title="Profile" description="Details from your authenticated session." />
          {isLoading && !profile && <SkeletonList rows={2} aria-label="Loading profile" />}
          {error && (
            <Card>
              <CardContent>
                <p className="text-sm text-destructive" role="alert">
                  Failed to load profile: {formatError(error)}
                </p>
              </CardContent>
            </Card>
          )}
          {profile && <ProfileCard key={profileKey(profile)} profile={profile} />}
        </Section>

        <Section aria-label="Team">
          <SectionHeader
            title="Team"
            description="Workspace memberships and invites are managed per-workspace."
          />
          <EmptyState
            icon={User}
            title="Team management lives in Workspaces"
            description="Open a workspace to invite teammates, adjust roles, and review pending invites."
          />
        </Section>
      </PageBody>
    </PageContainer>
  )
}

interface ProfileDetails {
  email: string | null
  name: string | null
  profileRole: string | null
  timezone: string | null
  avatarUrl: string | null
  isActive?: boolean
}

interface ProfileFormState {
  name: string
  profileRole: string
  timezone: string
  avatarUrl: string
}

const TIMEZONE_OPTIONS = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu',
  'UTC',
  'Europe/London',
  'Europe/Paris',
  'Asia/Tokyo',
] as const

function clean(value: string | null | undefined): string | null {
  const trimmed = value?.trim()
  return trimmed ? trimmed : null
}

function profileDetails(
  sessionUser: AppUser | null,
  backendUser: DashboardMe | undefined,
): ProfileDetails | null {
  const id = clean(sessionUser?.id) ?? clean(backendUser?.user_id)
  const email = clean(sessionUser?.email) ?? clean(backendUser?.email)
  const name = clean(backendUser?.name) ?? clean(sessionUser?.name)
  const profileRole = clean(backendUser?.profile_role)
  const timezone = clean(backendUser?.timezone)
  const avatarUrl = clean(backendUser?.avatar_url) ?? clean(sessionUser?.avatarUrl)

  if (!id && !email && !name) return null

  return {
    email,
    name,
    profileRole,
    timezone,
    avatarUrl,
    isActive: backendUser?.is_active,
  }
}

function ProfileCard({ profile }: { profile: ProfileDetails }) {
  const updateMe = useUpdateMe()
  const initialForm = useMemo(() => formFromProfile(profile), [profile])
  const [form, setForm] = useState<ProfileFormState>(initialForm)
  const [fileError, setFileError] = useState<string | null>(null)

  const display = userDisplayName({
    email: profile.email ?? '',
    name: form.name || profile.name,
    avatarUrl: form.avatarUrl || null,
  })
  const initials = userInitials({
    email: profile.email ?? '',
    name: form.name || profile.name,
    avatarUrl: form.avatarUrl || null,
  })
  const isDirty =
    form.name !== initialForm.name ||
    form.profileRole !== initialForm.profileRole ||
    form.timezone !== initialForm.timezone ||
    form.avatarUrl !== initialForm.avatarUrl
  const isReadOnly = profile.isActive === false

  const handleFileChange = async (file: File | undefined) => {
    setFileError(null)
    if (!file) return
    if (!file.type.startsWith('image/')) {
      setFileError('Choose a PNG, JPEG, WebP, or GIF image.')
      return
    }
    if (file.size > 750_000) {
      setFileError('Choose an image smaller than 750 KB.')
      return
    }
    try {
      const dataUrl = await readFileAsDataUrl(file)
      setForm((current) => ({ ...current, avatarUrl: dataUrl }))
    } catch {
      setFileError('Could not read that image.')
    }
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    try {
      await updateMe.mutateAsync({
        name: form.name.trim() || null,
        profile_role: form.profileRole.trim() || null,
        timezone: form.timezone || null,
        avatar_url: form.avatarUrl || null,
      })
      toast.success('Account settings saved')
    } catch (err) {
      toast.error(formatError(err))
    }
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          <span
            aria-hidden
            className="inline-flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-primary-soft text-sm font-medium text-primary-soft-foreground"
          >
            {form.avatarUrl ? (
              <img
                src={form.avatarUrl}
                alt=""
                className="size-full object-cover"
                referrerPolicy="no-referrer"
              />
            ) : (
              initials
            )}
          </span>
          <div className="min-w-0">
            <CardTitle className="truncate">{display}</CardTitle>
            {profile.email && (
              <CardDescription className="truncate">{profile.email}</CardDescription>
            )}
            {profile.isActive === false && (
              <p className="mt-1 text-xs text-muted-foreground">Account is read-only.</p>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="grid gap-4">
          <div className="grid gap-2">
            <Label htmlFor="account-name">Name</Label>
            <Input
              id="account-name"
              value={form.name}
              onChange={(event) =>
                setForm((current) => ({ ...current, name: event.target.value }))
              }
              disabled={isReadOnly || updateMe.isPending}
              placeholder="Your name"
              autoComplete="name"
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="account-role">Role</Label>
            <Input
              id="account-role"
              value={form.profileRole}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  profileRole: event.target.value,
                }))
              }
              disabled={isReadOnly || updateMe.isPending}
              placeholder="None"
              autoComplete="organization-title"
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="account-timezone">Timezone</Label>
            <select
              id="account-timezone"
              value={form.timezone}
              onChange={(event) =>
                setForm((current) => ({ ...current, timezone: event.target.value }))
              }
              disabled={isReadOnly || updateMe.isPending}
              className="h-10 rounded-md border border-input bg-card px-3 text-sm shadow-soft outline-none transition-[box-shadow,border-color] focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/30 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <option value="">None</option>
              {timezoneOptions(form.timezone).map((timezone) => (
                <option key={timezone} value={timezone}>
                  {timezoneLabel(timezone)}
                </option>
              ))}
            </select>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="account-avatar">Profile image</Label>
            <div className="flex flex-wrap items-center gap-2">
              <Button asChild type="button" variant="outline" size="sm">
                <label htmlFor="account-avatar" className="cursor-pointer">
                  <Upload />
                  Change image
                </label>
              </Button>
              {form.avatarUrl && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    setForm((current) => ({ ...current, avatarUrl: '' }))
                  }
                  disabled={isReadOnly || updateMe.isPending}
                >
                  <X />
                  Remove
                </Button>
              )}
            </div>
            <input
              id="account-avatar"
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif"
              className="sr-only"
              disabled={isReadOnly || updateMe.isPending}
              onChange={(event) => {
                void handleFileChange(event.target.files?.[0])
                event.target.value = ''
              }}
            />
            {fileError && (
              <p className="text-sm text-destructive" role="alert">
                {fileError}
              </p>
            )}
          </div>
          <div className="grid gap-1 text-sm">
            <span className="text-muted-foreground">Email</span>
            <span>{profile.email ?? 'Not provided'}</span>
          </div>
          <div className="flex justify-end">
            <Button
              type="submit"
              disabled={!isDirty || isReadOnly || updateMe.isPending}
            >
              {updateMe.isPending ? <Loader2 className="animate-spin" /> : <Save />}
              Save changes
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  )
}

function formFromProfile(profile: ProfileDetails): ProfileFormState {
  return {
    name: profile.name ?? '',
    profileRole: profile.profileRole ?? '',
    timezone: profile.timezone ?? '',
    avatarUrl: profile.avatarUrl ?? '',
  }
}

function profileKey(profile: ProfileDetails): string {
  return [
    profile.email,
    profile.name,
    profile.profileRole,
    profile.timezone,
    profile.avatarUrl,
    String(profile.isActive),
  ].join('|')
}

function timezoneOptions(current: string): string[] {
  const options = [...TIMEZONE_OPTIONS]
  if (current && !options.includes(current as (typeof TIMEZONE_OPTIONS)[number])) {
    return [current, ...options]
  }
  return options
}

function timezoneLabel(timezone: string): string {
  return timezone === 'UTC' ? 'UTC' : timezone.replace(/_/g, ' ')
}

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result)
      } else {
        reject(new Error('FileReader returned a non-string result'))
      }
    }
    reader.onerror = () => reject(reader.error ?? new Error('Could not read file'))
    reader.readAsDataURL(file)
  })
}
